import { useCallback, useEffect, useRef } from 'preact/hooks';

import WebApp from '@twa-dev/sdk';

import { useIntl } from '@/app/lib/localization/hooks/use-intl';
import { type AdsGramController, type ShowPromiseResult } from '@/app/types/adsgram';

import {
  useAppState,
  useGame,
  useUserProfile,
} from '@/shared/stores/app-store/app-store';

import { BLOCK_IDS_BY_GAME } from './constants';
import { resolveAdsGramInstance } from './lib/resolve-ads-gram-instance';
import { CustomErrorCode, ErrorWithCode, type UseAdvertisementProps } from './types';

export interface UseAdsgramParams {
  onReward?: <T>(value: T, result: ShowPromiseResult) => void;
  onError?: <T>(value: T, result: ShowPromiseResult) => void;

  onSkip?: <T>(value: T) => void;
}

const BLOCK_ID = import.meta.env.VITE_ADS_GRAM_KEY;
const BLOCK_ID_LITE = import.meta.env.VITE_ADS_GRAM_KEY_LITE;

export const useAdsgram = ({ onReward, onError, onSkip }: UseAdsgramParams = {}) => {
  const t = useIntl();

  const { isLiteMode } = useAppState();

  const game = useGame();
  const user = useUserProfile();

  let blockId = isLiteMode ? BLOCK_ID_LITE : BLOCK_ID;

  if (game?.key && BLOCK_IDS_BY_GAME[game.key]) {
    blockId = BLOCK_IDS_BY_GAME[game.key];
  }

  const controllerRef = useRef<AdsGramController | undefined>(undefined);

  const backButtonWasVisibleRef = useRef(WebApp.BackButton.isVisible);

  const makeError = useCallback(
    (
      state: ShowPromiseResult['state'],
      description: string,
      code?: string
    ): ShowPromiseResult => {
      return {
        error: true,
        done: false,
        state,
        description,
        code,
      };
    },
    []
  );

  useEffect(() => {
    const handleStart = () => {
      // store prev isVisible of BackButton to display it correctly after ads
      backButtonWasVisibleRef.current = WebApp.BackButton.isVisible;

      if (backButtonWasVisibleRef.current) WebApp.BackButton.hide();

      // Fix safe area padding for adsgram content
      if (blockId) {
        const adContainer = document.getElementById(blockId);

        if (!adContainer) return;

        adContainer.style.setProperty(
          '--adsgram-content-safe-area-top',
          'var(--tg-content-safe-area-inset-top, 0px)'
        );
        adContainer.style.setProperty(
          '--adsgram-safe-area-top',
          'var(--tg-safe-area-inset-top, 0px)'
        );

        adContainer.style.setProperty(
          '--adsgram-content-safe-area-bottom',
          'var(--tg-content-safe-area-inset-bottom, 0px)'
        );
        adContainer.style.setProperty(
          '--adsgram-safe-area-bottom',
          'var(--tg-safe-area-inset-bottom, 0px)'
        );

        adContainer.style.setProperty(
          '--adsgram-content-safe-area-left',
          'var(--tg-content-safe-area-inset-left, 0px)'
        );
        adContainer.style.setProperty(
          '--adsgram-safe-area-left',
          'var(--tg-safe-area-inset-left, 0px)'
        );

        adContainer.style.setProperty(
          '--adsgram-content-safe-area-right',
          'var(--tg-content-safe-area-inset-right, 0px)'
        );
        adContainer.style.setProperty(
          '--adsgram-safe-area-right',
          'var(--tg-safe-area-inset-right, 0px)'
        );
      }
    };

    const handleEnd = () => {
      if (backButtonWasVisibleRef.current) {
        WebApp.BackButton.show();
        backButtonWasVisibleRef.current = true;
      }
    };

    (async () => {
      const adsgram = await resolveAdsGramInstance();

      if (!adsgram || !blockId || !user) return;

      // WebApp.initDataUnsafe.user.id might be undefined, but it's required for AdsGram
      if (!WebApp.initDataUnsafe?.user?.id) {
        WebApp.initDataUnsafe.user = {
          ...WebApp.initDataUnsafe.user,
          first_name: WebApp.initDataUnsafe.user?.first_name ?? user.firstName,
          id: user.telegramId,
        };
      }

      controllerRef.current = adsgram.init({
        blockId,
        debug: import.meta.env.DEV,
        debugBannerType: 'FullscreenMedia',
      });

      controllerRef.current.addEventListener('onStart', handleStart);

      controllerRef.current.addEventListener('onError', handleEnd);
      controllerRef.current.addEventListener('onComplete', handleEnd);
    })();

    return () => {
      controllerRef.current?.removeEventListener('onStart', handleStart);

      controllerRef.current?.removeEventListener('onError', handleEnd);
      controllerRef.current?.removeEventListener('onComplete', handleEnd);

      controllerRef.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user]);

  const show = useCallback(
    <T>(
      value: T,
      {
        onReward: innerOnReward,
        onError: innerOnError,
        onSkip: innerOnSkip,
      }: UseAdvertisementProps = {}
    ) => {
      const controller = controllerRef.current;

      return new Promise<ShowPromiseResult>((resolve, reject) => {
        if (!controller) {
          const error = makeError(
            'load',
            blockId ? 'Adsgram script not loaded' : 'Block ID is not provided'
          );

          onError?.(value, error);

          innerOnError?.(new ErrorWithCode(error.description));

          return reject(error);
        }

        const handleAdsNotFound = () => {
          const err = makeError(
            'render',
            t('Oops, no ads available right now'),
            CustomErrorCode.NO_AVAILABLE_ADS
          );

          console.error(err);

          innerOnError?.(new ErrorWithCode(err.description, err.code));
          onError?.(value, err);
        };

        controller.addEventListener('onBannerNotFound', handleAdsNotFound);

        return controller
          .show()
          .then((res) => {
            if (res.done) {
              innerOnReward?.();
              onReward?.(value, res);
            } else {
              innerOnSkip?.();
              onSkip?.(value);
            }

            resolve(res);
          })
          .catch((err: ShowPromiseResult) => {
            console.error(err);

            innerOnError?.(new ErrorWithCode(err.description));
            onError?.(value, err);

            reject(err.description);
          })
          .finally(() => {
            controller.removeEventListener('onBannerNotFound', handleAdsNotFound);
          });
      });
    },
    [blockId, makeError, onError, onReward, onSkip, t]
  );

  return show;
};
